/*
 * Smart lists (saved client filters for campaigns and quick lists) and the complaints log with follow-up (draft 6c).
 */
import { z } from "zod";

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const smartFilter = z
  .object({
    status: z.enum(["active", "inactive", "all"]).default("active"),
    /** one_time: only one-time jobs; repeating: has a repeating job. */
    plan: z.enum(["any", "one_time", "repeating"]).default("any"),
    tags: z.array(z.string().max(40)).max(20).default([]),
    zips: z
      .array(z.string().regex(/^\d{5}$/))
      .max(200)
      .default([]),
    /** Last finished visit at least this many days ago. */
    lastVisitMinDays: z.number().int().min(0).max(3650).nullable().default(null),
    /** Last finished visit at most this many days ago. */
    lastVisitMaxDays: z.number().int().min(0).max(3650).nullable().default(null),
    /** Leave out people who said no to marketing (on unless turned off for a service message list). */
    marketingOnly: z.boolean().default(true),
  })
  .strict();
export type SmartFilter = z.infer<typeof smartFilter>;

export const smartListInput = z
  .object({ name: z.string().trim().min(1, "Name the list.").max(80), filter: smartFilter })
  .strict();
export type SmartListInput = z.infer<typeof smartListInput>;

export const smartListRow = z.object({ id: z.uuid(), name: z.string(), filter: smartFilter, count: z.number() });
export type SmartListRow = z.infer<typeof smartListRow>;

export const smartListClients = z.object({
  clients: z.array(z.object({ id: z.uuid(), name: z.string(), phone: z.string().nullable(), lastVisit: z.string().nullable() })),
});

export const complaintInput = z
  .object({
    clientId: z.uuid(),
    jobId: z.uuid().nullable().optional(),
    visitId: z.uuid().nullable().optional(),
    what: z.string().trim().min(1, "Say what went wrong.").max(3000),
    followUpOn: day.nullable().optional(),
  })
  .strict();
export type ComplaintInput = z.infer<typeof complaintInput>;

export const complaintPatch = z
  .object({
    status: z.enum(["open", "closed"]).optional(),
    fix: z.string().trim().max(3000).nullable().optional(),
    followUpOn: day.nullable().optional(),
  })
  .strict();
export type ComplaintPatch = z.infer<typeof complaintPatch>;

export const complaintRow = z.object({
  id: z.uuid(),
  clientId: z.uuid(),
  clientName: z.string(),
  jobId: z.uuid().nullable(),
  visitId: z.uuid().nullable(),
  what: z.string(),
  status: z.enum(["open", "closed"]),
  followUpOn: z.string().nullable(),
  fix: z.string().nullable(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
});
export type ComplaintRow = z.infer<typeof complaintRow>;
